import { Box, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { MAX_NOTE_LENGTH } from './useReviewDraft';

interface SessionNoteDockProps {
    sessionLabel: string;
    value: string;
    onChange: (note: string) => void;
    saveStatus: string;
    overallNote: string;
    onOverallChange: (note: string) => void;
    variant: 'inline' | 'sheet';
}

const DOCK_WIDTH = 320;

const SessionNoteDock = ({ sessionLabel, value, onChange, saveStatus, variant, overallNote, onOverallChange }: SessionNoteDockProps) => {
    const [tab, setTab] = useState(0);
    const isInline = variant === 'inline';
    const note = tab === 0 ? value : overallNote;
    const remaining = MAX_NOTE_LENGTH - note.length;
    const showCounter = remaining <= MAX_NOTE_LENGTH * 0.2;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflowY: 'auto',
                bgcolor: 'background.paper',
                ...(isInline
                    ? { width: DOCK_WIDTH, flexShrink: 0, borderLeft: '1px solid', borderColor: 'divider' }
                    : { width: '100%' }),
            }}
        >
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                <Tabs value={tab} onChange={(_, next) => setTab(next)} aria-label='Review notes' sx={{ mb: 2, '& .MuiTabs-indicator': { bgcolor: 'primary.main' } }}>
                    <Tab label='Session' id='session-note-tab' aria-controls='review-note-panel' />
                    <Tab label='Overall' id='overall-note-tab' aria-controls='review-note-panel' />
                </Tabs>
                <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                    Your note
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                    {tab === 0 ? sessionLabel : 'Case review'}
                </Typography>
            </Box>

            <Box id='review-note-panel' role='tabpanel' aria-labelledby={tab === 0 ? 'session-note-tab' : 'overall-note-tab'} sx={{ px: 2, pb: 1 }}>
                <TextField
                    value={note}
                    onChange={(event) => tab === 0 ? onChange(event.target.value) : onOverallChange(event.target.value)}
                    label={tab === 0 ? 'Session note' : 'Overall note'}
                    multiline
                    fullWidth
                    inputProps={{ maxLength: MAX_NOTE_LENGTH }}
                    minRows={isInline ? 10 : 3}
                    maxRows={isInline ? 18 : 6}
                />
            </Box>

            <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                sx={{ px: 2, pb: 2, minHeight: 24 }}
            >
                <Typography role='status' variant='caption' color='text.secondary'>{saveStatus}</Typography>
                {showCounter && (
                    <Typography variant='caption' color={remaining === 0 ? 'error.main' : 'text.secondary'}>
                        {`${remaining} left`}
                    </Typography>
                )}
            </Stack>
        </Box>
    );
};

export default SessionNoteDock;
